import { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom-v5-compat";
import {
  setSupportProductInfo,
  TravelCategory,
} from "@b2bportal/core-self-serve";
import { TRIPS_PATH } from "@overrides/utilities";
import { hotelsPostBookingApi } from "../../store";
import type {
  HotelReservationPageContainerProps,
  HotelReservationPageHookProps,
} from "./types";

export const useHotelReservationPageRoute = () => {
  const { reservationId } = useParams<{ reservationId: string }>();
  if (reservationId == null || reservationId === "") {
    throw new Error("Missing or empty reservationId parameter");
  }
  return { reservationId };
};

export const useHotelReservationPage = (
  props: HotelReservationPageHookProps
) => {
  return useHotelReservationPageContainer({
    ...props,
    tripsRoute: TRIPS_PATH,
  });
};

export const useHotelReservationPageContainer = ({
  reservationId,
  tripsRoute,
}: HotelReservationPageContainerProps & { tripsRoute: string }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const navigateToTrips = useCallback(() => {
    navigate(tripsRoute, { replace: true });
  }, [tripsRoute, navigate]);

  const {
    data: hotelReservation,
    isLoading,
    isError,
  } = hotelsPostBookingApi.useFetchHotelReservationQuery({
    reservationBookingId: reservationId ?? "",
  });

  useEffect(() => {
    if (isError) {
      dispatch(setSupportProductInfo());
      return navigateToTrips();
    }

    dispatch(
      setSupportProductInfo({
        productId: reservationId,
        productType: TravelCategory.Lodging,
      })
    );

    return () => {
      dispatch(setSupportProductInfo());
    };
  }, [isError, dispatch, navigateToTrips, reservationId]);

  return useMemo(() => {
    return {
      context: {
        hotelReservation,
        isLoading,
        isError,
      },
    };
  }, [hotelReservation, isLoading, isError]);
};
